import { differenceInDays, startOfDay } from 'date-fns'
import { useTheme } from 'styled-components'

import { type Task, TaskStatus } from '../types/task.types'
/**
 * Returns the appropriate color for a task's status based on its state and expiry date.
 * This hook is memoized and re-calculates only if 'task' or 'theme' change.
 *
 * For tasks with `TaskStatus.Expired`:
 * - Color: `theme.Components.Chip.PrimaryWarning.Text`
 *
 * For tasks with `TaskStatus.Completed`:
 * - Color: `theme.Colors.Neutrals[500]`
 *
 * For tasks with `TaskStatus.Pending`:
 * - If `task.expiresAt` is not defined or task is recurring/repeating:
 *   Color: `theme.Components.Chip.Secondary.Text`
 * - If `task.expiresAt` is defined:
 *   - Expired (daysTillExpiry < 0):
 *     Color: `theme.Components.Chip.Secondary.Text`
 *   - Expires today (daysTillExpiry === 0):
 *     Color: `theme.Components.Chip.PrimaryWarning.Text`
 *   - Expires tomorrow or later (daysTillExpiry >= 1):
 *     Color: `theme.Components.Chip.SecondaryWarning.Text`
 *
 * For all other statuses:
 * - Color: `theme.Components.Chip.Secondary.Text`
 *
 * @param task The task object, expected to have `status`, `expiresAt` (optional), `recurring`, and `repeating` properties
 * @returns The color string from the theme
 */
export const useTaskStatusMetaData = (
  task: Task,
): {
  color: string
  daysTillExpiry?: number
  status: TaskStatus
} => {
  const theme = useTheme()

  switch (task.status) {
    case TaskStatus.Expired: {
      if (task.type === 'Hylyght' && task.activity?.type === 'program') {
        return {
          color: theme.Colors.Neutrals.Darker,
          status: TaskStatus.Expired,
        }
      }

      return {
        color: theme.Components.Chip.PrimaryWarning.Text,
        status: TaskStatus.Expired,
      }
    }

    case TaskStatus.Completed: {
      return {
        color: theme.Colors.Neutrals[500],
        status: TaskStatus.Completed,
      }
    }

    case TaskStatus.Pending: {
      // Case 1: No specific expiry date is set for the pending task.
      // Show status relative to the planned date.
      if (!task.expiresAt || task.recurring || task.repeating) {
        return {
          color: theme.Colors.Neutrals.Darker,
          status: TaskStatus.Pending,
        }
      }

      // Case 2: An expiry date is set. Calculate days until expiry.
      // Using startOfDay for robust comparison, ignoring time components.
      const today = startOfDay(new Date())
      const expiryDate = startOfDay(new Date(task.expiresAt)) // Ensures task.expiresAt is treated as a Date
      const daysTillExpiry = differenceInDays(expiryDate, today)

      if (daysTillExpiry < 0) {
        return {
          color: theme.Colors.Neutrals.Darker,
          status: TaskStatus.Pending,
        }
      } else if (daysTillExpiry === 0) {
        return {
          color: theme.Components.Chip.PrimaryWarning.Text,
          status: TaskStatus.Pending,
          daysTillExpiry,
        }
      } else {
        return {
          color: theme.Components.Chip.SecondaryWarning.Text,
          status: TaskStatus.Pending,
          daysTillExpiry,
        }
      }
    }
    default: {
      return {
        color: theme.Colors.Neutrals.Darker,
        status: TaskStatus.Pending,
      }
    }
  }
}
